import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import useSeo from '@/hooks/useSeo';

const HERO_IMG = '/images/CASES/HERO/HERO-CASES.webp';
const INSPECAO_COROA = '/images/CASES/manutencao-transportadores-fardos/GALERIA/INSPECAO-COROA-TRANSPORTADOR.webp';
const SUBSTITUICAO_CORRENTE = '/images/CASES/manutencao-transportadores-fardos/GALERIA/SUBSTITUICAO-CORRENTE-TRANSPORTADOR.webp';
const SUBSTITUICAO_CORRENTE2 = '/images/CASES/manutencao-transportadores-fardos/GALERIA/SUBSTITUICAO-CORRENTE-TRANSPORTADOR2.webp';
const REPARO_CALDEIRARIA = '/images/CASES/manutencao-transportadores-fardos/GALERIA/REPARO-CALDEIRARIA-TRANSPORTADOR.webp';
const REPARO_CALDEIRARIA2 = '/images/CASES/manutencao-transportadores-fardos/GALERIA/REPARO-CALDEIRARIA-TRANSPORTADOR2.webp';
const INSTALACAO_CORRENTE = '/images/CASES/manutencao-transportadores-fardos/GALERIA/INSTALACAO-CORRENTE-TRANSPORTADOR.webp';
const SUBSTITUICAO_GUIA = '/images/CASES/manutencao-transportadores-fardos/GALERIA/SUBSTITUICAO-GUIA-TEFLON-TRANSPORTADOR.webp';
const GUIAS_SUBSTITUIDAS = '/images/CASES/manutencao-transportadores-fardos/GALERIA/GUIAS-SUBSTITUIDAS-TRANSPORTADOR.webp';
const SUBSTITUICAO_MOTORREDUTOR = '/images/CASES/manutencao-transportadores-fardos/GALERIA/SUBSTITUICAO-MOTORREDUTOR-TRANSPORTADOR.webp';
const SUBSTITUICAO_CORRENTE_VENTILADOR = '/images/CASES/manutencao-transportadores-fardos/GALERIA/SUBSTITUICAO-CORRENTE-VENTILADOR.webp';

export default function CaseManutencaoTransportadoresFardosPage() {
  useSeo({
    title: 'Substituição de Correntes em Transportadores Industriais | Case Pilar',
    description: 'Conheça como a Pilar executa a substituição de correntes, barras de desgaste, redutores e componentes mecânicos em transportadores de fardos durante Paradas Gerais na indústria de papel e celulose.',
    keywords: 'transportador de fardos, correntes industriais, manutenção mecânica industrial, transportadores industriais, substituição de correntes, barras de desgaste, redutores industriais, motores industriais, papel e celulose, parada geral, Grupo Pilar, Aracruz, ES',
    canonical: 'https://www.pilarmanutencao.com.br/cases/manutencao-transportadores-fardos-substituicao-correntes',
    ogUrl: 'https://www.pilarmanutencao.com.br/cases/manutencao-transportadores-fardos-substituicao-correntes',
    ogImage: HERO_IMG,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gp-graphite">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative min-h-[60vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={HERO_IMG}
              alt="Transportador de fardos após manutenção mecânica com instalação de novos componentes de desgaste e transmissão"
              title="Transportador de Fardos — Case Pilar"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gp-graphite/75"></div>
          </div>
          <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 pt-28 pb-16">
            <div className="max-w-4xl">
              <div className="flex items-center gap-3 mb-6 flex-wrap">
                <span className="inline-block px-3 py-1 rounded-sm text-xs font-semibold uppercase tracking-wider bg-gp-blue text-white">
                  Serviços de Campo
                </span>
                <span className="text-gray-400 text-xs uppercase tracking-wider">Papel e Celulose</span>
                <span className="text-gray-500 text-xs">— Aracruz, ES</span>
              </div>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase leading-tight mb-6">
                <span className="text-gp-yellow">Case:</span>{' '}
                Manutenção Mecânica e Substituição de Correntes em Transportadores de Fardos
              </h1>
              <p className="text-gray-300 text-lg max-w-2xl leading-relaxed">
                Substituição de correntes, barras de desgaste, redutores e componentes mecânicos em transportadores de fardos durante Parada Geral, com serviços integrados de caldeiraria e soldagem em indústria de papel e celulose.
              </p>
            </div>
          </div>
        </section>

        {/* Visão Geral */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Visão Geral</h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div className="bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-6 md:p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-information-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-white uppercase mb-2">Dados do Projeto</h3>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-settings-3-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Equipamento</span>
                        <p className="text-gray-300 text-sm">Transportadores de Fardos</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-team-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Equipe</span>
                        <p className="text-gray-300 text-sm">Equipe especializada de manutenção mecânica industrial</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-calendar-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Período</span>
                        <p className="text-gray-300 text-sm">Parada Geral</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-building-2-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Segmento</span>
                        <p className="text-gray-300 text-sm">Papel e Celulose</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter bg-gp-graphite">
                  <img
                    src={INSPECAO_COROA}
                    alt="Inspeção técnica dos componentes de transmissão para avaliação das condições operacionais do transportador"
                    title="Inspeção técnica dos componentes de transmissão do transportador"
                    className="w-full h-80 md:h-[28rem] object-contain"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Inspeção técnica dos componentes de transmissão para avaliação das condições operacionais do transportador
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Desafio da Manutenção */}
        <section className="relative py-20 bg-gp-graphite-light border-t border-gp-graphite-lighter border-b border-gp-graphite-lighter">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Desafio da Manutenção</h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div className="bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-6 md:p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-alert-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="text-gray-300 text-base leading-relaxed mb-4">
                        Os transportadores de fardos são equipamentos de grande porte submetidos a condições severas de operação contínua, com componentes sujeitos a desgaste natural pelo atrito e esforço mecânico. A intervenção durante a Parada Geral exigia a substituição das correntes transportadoras e barras de desgaste, além de inspeções detalhadas dos conjuntos mecânicos e serviços complementares de caldeiraria e soldagem.
                      </p>
                      <p className="text-gray-400 text-sm leading-relaxed mb-4">
                        As principais complexidades envolviam:
                      </p>
                      <ul className="space-y-2 mb-4">
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Equipamentos de grande porte com componentes pesados e de difícil acesso</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Componentes sujeitos a desgaste contínuo, exigindo diagnóstico preciso durante a parada</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Substituição de correntes industriais com ajustes de tensionamento e alinhamento</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Ajustes mecânicos de precisão nos conjuntos de transmissão e acionamento</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Integração com atividades de caldeiraria para reforços e reparos estruturais</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Integração com atividades de soldagem para fixação de componentes e reparos</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Janela reduzida de parada com múltiplas atividades simultâneas em diferentes transportadores</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Coordenação de múltiplas frentes de trabalho com equipes de diferentes disciplinas</span>
                        </li>
                      </ul>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        O principal desafio era maximizar a disponibilidade dos transportadores de fardos após o retorno da operação, garantindo que todas as intervenções fossem executadas com segurança, precisão técnica e dentro da janela da Parada Geral.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter bg-gp-graphite">
                  <img
                    src={SUBSTITUICAO_CORRENTE}
                    alt="Sistema apresentando desgaste operacional e necessidade de substituição dos componentes críticos de transmissão"
                    title="Componentes críticos de transmissão com desgaste operacional"
                    className="w-full h-80 md:h-[28rem] object-contain"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Sistema apresentando desgaste operacional e necessidade de substituição dos componentes críticos de transmissão
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Planejamento da Parada */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Planejamento da Parada</h2>
              </div>

              <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter bg-gp-graphite mb-8">
                <img
                  src={SUBSTITUICAO_MOTORREDUTOR}
                  alt="Troca de moto-redutor realizada como parte do escopo de recuperação do sistema transportador"
                  title="Substituição de moto-redutor do transportador"
                  className="w-full h-80 md:h-[28rem] object-contain"
                  loading="lazy"
                />
                <div className="bg-gp-graphite p-3 border-t border-gp-graphite-lighter">
                  <p className="text-gray-400 text-xs text-center">
                    Troca de moto-redutor realizada como parte do escopo de recuperação do sistema transportador
                  </p>
                </div>
              </div>

              <p className="text-gray-300 text-base leading-relaxed mb-6">
                A complexidade da manutenção dos transportadores de fardos durante a Parada Geral, envolvendo múltiplas disciplinas e equipamentos de grande porte, exigiu um planejamento detalhado e antecipado. A Pilar executou as seguintes etapas preparatórias:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">A</span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-sm uppercase mb-1">Planejamento da Intervenção</h4>
                    <p className="text-gray-400 text-sm leading-relaxed">Definição do escopo completo da atividade, dimensionamento de recursos para múltiplas frentes de trabalho, elaboração de cronograma integrado à Parada Geral e análise de riscos para equipamentos de grande porte.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">B</span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-sm uppercase mb-1">Mobilização da Equipe</h4>
                    <p className="text-gray-400 text-sm leading-relaxed">Mobilização de equipe especializada de manutenção mecânica industrial com experiência em transportadores, correntes industriais, redutores e motores em Paradas Gerais de papel e celulose.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">C</span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-sm uppercase mb-1">Coordenação Multidisciplinar</h4>
                    <p className="text-gray-400 text-sm leading-relaxed">Integração do planejamento com as equipes de caldeiraria e soldagem, definindo sequenciamento das atividades e pontos de interface entre as disciplinas durante a Parada Geral.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">D</span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-sm uppercase mb-1">Preparação de Recursos</h4>
                    <p className="text-gray-400 text-sm leading-relaxed">Preparação de ferramentas, dispositivos de movimentação e equipamentos necessários para a substituição de correntes, barras de desgaste, redutores e motores durante a janela da Parada Geral.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Substituição das Correntes */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Substituição das Correntes</h2>
              </div>

              <div className="rounded-lg overflow-hidden border border-gp-yellow/20 bg-gp-graphite mb-8">
                <img
                  src={SUBSTITUICAO_CORRENTE2}
                  alt="Substituição completa dos componentes de transmissão visando confiabilidade operacional"
                  title="Substituição completa dos componentes de transmissão"
                  className="w-full h-80 md:h-[28rem] object-contain"
                  loading="lazy"
                />
                <div className="bg-gp-graphite p-3 border-t border-gp-graphite-lighter">
                  <p className="text-gray-400 text-xs text-center">
                    Substituição completa dos componentes de transmissão visando confiabilidade operacional
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div className="bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-6 md:p-8">
                  <p className="text-gray-300 text-base leading-relaxed mb-6">
                    A atividade central da intervenção foi a substituição das correntes transportadoras dos transportadores de fardos. As correntes originais apresentavam desgaste natural pelo tempo de operação contínua, comprometendo a eficiência da movimentação dos fardos e a confiabilidade do sistema de transporte.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-4">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-search-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <p className="text-gray-300 text-sm leading-relaxed pt-1">Inspeção e avaliação das correntes transportadoras existentes com identificação dos trechos críticos de desgaste</p>
                    </div>
                    <div className="flex items-start gap-3 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-4">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-tools-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <p className="text-gray-300 text-sm leading-relaxed pt-1">Remoção das correntes desgastadas e preparação dos assentos e guias para instalação das novas correntes</p>
                    </div>
                    <div className="flex items-start gap-3 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-4">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-double-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <p className="text-gray-300 text-sm leading-relaxed pt-1">Instalação e tensionamento das novas correntes transportadoras com ajustes de alinhamento e verificação de funcionamento</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gp-graphite border border-gp-yellow/20 rounded-lg p-6 md:p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-focus-2-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-white uppercase mb-2">Destaque Técnico</h3>
                    </div>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed mb-4">
                    A atividade envolveu a recuperação das condições operacionais dos transportadores de fardos através da substituição de componentes de desgaste e execução de intervenções mecânicas corretivas e preventivas.
                  </p>
                  <p className="text-gray-400 text-xs leading-relaxed">
                    As ações foram planejadas para maximizar a disponibilidade dos equipamentos após o retorno da operação, com foco na confiabilidade do sistema de transporte de fardos.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Intervenções Mecânicas Complementares */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Intervenções Mecânicas Complementares</h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter bg-gp-graphite">
                  <img
                    src={INSTALACAO_CORRENTE}
                    alt="Instalação e ajuste dos novos componentes de transmissão do transportador"
                    title="Instalação dos novos componentes de transmissão"
                    className="w-full h-72 md:h-80 object-contain"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Instalação e ajuste dos novos componentes de transmissão do transportador
                    </p>
                  </div>
                </div>

                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter bg-gp-graphite">
                  <img
                    src={SUBSTITUICAO_GUIA}
                    alt="Troca das guias de desgaste para restabelecimento das condições operacionais"
                    title="Substituição das guias de desgaste"
                    className="w-full h-72 md:h-80 object-contain"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Troca das guias de desgaste para restabelecimento das condições operacionais
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-6 md:p-8">
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <span className="text-gp-yellow font-display font-bold text-sm">ETAPA 1</span>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold text-sm uppercase mb-1">Substituição das Barras de Desgaste</h4>
                      <p className="text-gray-400 text-sm leading-relaxed">Remoção das barras de desgaste existentes e instalação de novas barras nos transportadores de fardos, garantindo a proteção da estrutura e o correto deslizamento das correntes transportadoras.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <span className="text-gp-yellow font-display font-bold text-sm">ETAPA 2</span>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold text-sm uppercase mb-1">Inspeção dos Conjuntos Mecânicos</h4>
                      <p className="text-gray-400 text-sm leading-relaxed">Inspeção detalhada de todos os conjuntos mecânicos dos transportadores, incluindo sistemas de transmissão, acionamentos, eixos e mancais, com verificação de desgastes, folgas e condições gerais.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <span className="text-gp-yellow font-display font-bold text-sm">ETAPA 3</span>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold text-sm uppercase mb-1">Troca de Redutores e Motores</h4>
                      <p className="text-gray-400 text-sm leading-relaxed">Substituição de redutores e motores quando necessário, conforme resultado das inspeções, garantindo a confiabilidade dos sistemas de acionamento dos transportadores de fardos.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <span className="text-gp-yellow font-display font-bold text-sm">ETAPA 4</span>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold text-sm uppercase mb-1">Ajustes de Alinhamento</h4>
                      <p className="text-gray-400 text-sm leading-relaxed">Ajustes de alinhamento dos conjuntos de transmissão e acionamento, garantindo o correto posicionamento de todos os componentes para operação confiável dos transportadores.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <span className="text-gp-yellow font-display font-bold text-sm">ETAPA 5</span>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold text-sm uppercase mb-1">Ajustes Mecânicos Diversos</h4>
                      <p className="text-gray-400 text-sm leading-relaxed">Execução de ajustes mecânicos diversos identificados durante as inspeções, incluindo regulagens, fixações e correções de folgas nos conjuntos dos transportadores de fardos.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Serviços de Caldeiraria e Soldagem */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Serviços de Caldeiraria e Soldagem</h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter bg-gp-graphite">
                  <img
                    src={REPARO_CALDEIRARIA}
                    alt="Execução de serviços de caldeiraria para recuperação estrutural do transportador"
                    title="Serviços de caldeiraria no transportador"
                    className="w-full h-72 md:h-80 object-contain"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Execução de serviços de caldeiraria para recuperação estrutural do transportador
                    </p>
                  </div>
                </div>

                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter bg-gp-graphite">
                  <img
                    src={REPARO_CALDEIRARIA2}
                    alt="Correções estruturais e reforços mecânicos aplicados durante a recuperação do equipamento"
                    title="Reforços mecânicos durante a recuperação"
                    className="w-full h-72 md:h-80 object-contain"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Correções estruturais e reforços mecânicos aplicados durante a recuperação do equipamento
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div className="bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-6 md:p-8">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-hammer-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-white uppercase mb-2">Caldeiraria Industrial</h3>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        Serviços de caldeiraria executados para reforços estruturais, confecção e ajustes de componentes metálicos, reparos em estruturas de suporte e adequações necessárias nos transportadores de fardos durante a Parada Geral.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-6 md:p-8">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-flashlight-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-white uppercase mb-2">Soldagem Industrial</h3>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        Serviços de soldagem para fixação de componentes, reparos em estruturas metálicas, soldas de reforço nos pontos críticos identificados durante as inspeções e garantia da integridade estrutural dos transportadores de fardos.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Resultado Obtido */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Resultado Obtido</h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div className="rounded-lg overflow-hidden border border-gp-yellow/20 bg-gp-graphite">
                  <img
                    src={GUIAS_SUBSTITUIDAS}
                    alt="Transportador com guias substituídas e pronto para retorno à operação industrial"
                    title="Transportador com guias substituídas — pronto para operação"
                    className="w-full h-80 md:h-[28rem] object-contain"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Transportador com guias substituídas e pronto para retorno à operação industrial
                    </p>
                  </div>
                </div>

                <div className="relative rounded-lg overflow-hidden border border-gp-yellow/20 bg-gp-graphite-light p-6 md:p-8">
                  <div className="absolute top-0 left-0 w-1 h-full bg-gp-yellow"></div>
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-trophy-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h3 className="font-display text-xl font-bold text-white uppercase mb-2">Resultados</h3>
                    </div>
                  </div>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-tools-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Correntes Substituídas</span>
                        <p className="text-gray-400 text-sm">Substituição completa das correntes transportadoras com tensionamento e alinhamento adequados</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-shield-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Barras de Desgaste</span>
                        <p className="text-gray-400 text-sm">Substituição das barras de desgaste garantindo proteção estrutural e correto deslizamento das correntes</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-search-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Conjuntos Inspecionados</span>
                        <p className="text-gray-400 text-sm">Inspeção completa dos conjuntos mecânicos com diagnóstico e intervenções corretivas e preventivas</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-settings-3-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Redutores e Motores</span>
                        <p className="text-gray-400 text-sm">Troca de redutores e motores quando necessário, garantindo confiabilidade do sistema de acionamento</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-hammer-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Caldeiraria e Soldagem</span>
                        <p className="text-gray-400 text-sm">Serviços integrados de caldeiraria e soldagem executados com qualidade e segurança operacional</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-calendar-check-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Prazo da Parada Geral</span>
                        <p className="text-gray-400 text-sm">Cronograma de parada cumprido com retorno seguro dos transportadores de fardos à operação</p>
                      </div>
                    </li>
                  </ul>
                  <div className="mt-6 pt-4 border-t border-gp-graphite-lighter bg-gp-graphite/40 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-information-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <p className="text-gray-400 text-xs leading-relaxed">
                        Importante: A Pilar não executou modernização ou reforma completa dos transportadores. O escopo da Pilar concentrou-se em manutenção mecânica industrial durante Parada Geral: inspeção mecânica, substituição de correntes e barras de desgaste, inspeção dos conjuntos mecânicos, ajustes mecânicos, serviços de caldeiraria e soldagem, troca de redutores e motores quando necessário, ajustes de alinhamento e inspeções finais para retorno operacional. O foco foi a recuperação das condições operacionais e maximização da disponibilidade dos equipamentos.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Diferenciais Técnicos da Pilar */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Diferenciais Técnicos da Pilar</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-2-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Experiência comprovada em Paradas Gerais de plantas de papel e celulose com intervenções em transportadores e equipamentos de grande porte</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-tools-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Capacidade técnica em substituição de correntes industriais, barras de desgaste e componentes de transportadores de grande porte</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-search-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Inspeção técnica detalhada de conjuntos mecânicos com diagnóstico preciso para tomada de decisão durante paradas</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-hammer-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Integração de serviços de caldeiraria industrial e soldagem com manutenção mecânica em uma única intervenção</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-compass-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Alinhamento e ajustes de precisão em sistemas de transmissão e acionamento de transportadores industriais</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-team-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Coordenação de múltiplas frentes de trabalho com equipes especializadas em Paradas Gerais e janelas reduzidas</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Galeria Técnica */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Galeria Técnica</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="relative rounded-lg overflow-hidden border border-gp-graphite-lighter bg-gp-graphite">
                  <img
                    src={HERO_IMG}
                    alt="Transportador de fardos após manutenção mecânica com instalação de novos componentes de desgaste e transmissão"
                    className="w-full h-64 object-contain"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Transportador de fardos após manutenção mecânica com instalação de novos componentes de desgaste e transmissão
                    </p>
                  </div>
                </div>
                <div className="relative rounded-lg overflow-hidden border border-gp-graphite-lighter bg-gp-graphite">
                  <img
                    src={INSPECAO_COROA}
                    alt="Inspeção técnica dos componentes de transmissão para avaliação das condições operacionais do transportador"
                    className="w-full h-64 object-contain"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Inspeção técnica dos componentes de transmissão para avaliação das condições operacionais
                    </p>
                  </div>
                </div>
                <div className="relative rounded-lg overflow-hidden border border-gp-graphite-lighter bg-gp-graphite">
                  <img
                    src={SUBSTITUICAO_CORRENTE}
                    alt="Sistema apresentando desgaste operacional e necessidade de substituição dos componentes críticos de transmissão"
                    className="w-full h-64 object-contain"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Sistema com desgaste operacional — necessidade de substituição dos componentes críticos de transmissão
                    </p>
                  </div>
                </div>
                <div className="relative rounded-lg overflow-hidden border border-gp-graphite-lighter bg-gp-graphite">
                  <img
                    src={SUBSTITUICAO_CORRENTE2}
                    alt="Substituição completa dos componentes de transmissão visando confiabilidade operacional"
                    className="w-full h-64 object-contain"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Substituição completa dos componentes de transmissão visando confiabilidade operacional
                    </p>
                  </div>
                </div>
                <div className="relative rounded-lg overflow-hidden border border-gp-graphite-lighter bg-gp-graphite">
                  <img
                    src={REPARO_CALDEIRARIA}
                    alt="Execução de serviços de caldeiraria para recuperação estrutural do transportador"
                    className="w-full h-64 object-contain"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Execução de serviços de caldeiraria para recuperação estrutural do transportador
                    </p>
                  </div>
                </div>
                <div className="relative rounded-lg overflow-hidden border border-gp-graphite-lighter bg-gp-graphite">
                  <img
                    src={REPARO_CALDEIRARIA2}
                    alt="Correções estruturais e reforços mecânicos aplicados durante a recuperação do equipamento"
                    className="w-full h-64 object-contain"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Correções estruturais e reforços mecânicos aplicados durante a recuperação
                    </p>
                  </div>
                </div>
                <div className="relative rounded-lg overflow-hidden border border-gp-graphite-lighter bg-gp-graphite">
                  <img
                    src={INSTALACAO_CORRENTE}
                    alt="Instalação e ajuste dos novos componentes de transmissão do transportador"
                    className="w-full h-64 object-contain"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Instalação e ajuste dos novos componentes de transmissão do transportador
                    </p>
                  </div>
                </div>
                <div className="relative rounded-lg overflow-hidden border border-gp-graphite-lighter bg-gp-graphite">
                  <img
                    src={SUBSTITUICAO_GUIA}
                    alt="Troca das guias de desgaste para restabelecimento das condições operacionais"
                    className="w-full h-64 object-contain"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Troca das guias de desgaste para restabelecimento das condições operacionais
                    </p>
                  </div>
                </div>
                <div className="relative rounded-lg overflow-hidden border border-gp-graphite-lighter bg-gp-graphite">
                  <img
                    src={GUIAS_SUBSTITUIDAS}
                    alt="Transportador com guias substituídas e pronto para retorno à operação industrial"
                    className="w-full h-64 object-contain"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Transportador com guias substituídas e pronto para retorno à operação industrial
                    </p>
                  </div>
                </div>
                <div className="relative rounded-lg overflow-hidden border border-gp-graphite-lighter bg-gp-graphite">
                  <img
                    src={SUBSTITUICAO_MOTORREDUTOR}
                    alt="Troca de moto-redutor realizada como parte do escopo de recuperação do sistema transportador"
                    className="w-full h-64 object-contain"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Troca de moto-redutor realizada como parte do escopo de recuperação
                    </p>
                  </div>
                </div>
                <div className="relative rounded-lg overflow-hidden border border-gp-graphite-lighter bg-gp-graphite md:col-span-2 lg:col-span-3">
                  <img
                    src={SUBSTITUICAO_CORRENTE_VENTILADOR}
                    alt="Substituição de corrente em ventilador — atividade integrada ao escopo de manutenção"
                    className="w-full h-64 object-contain"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Substituição de corrente em ventilador — atividade integrada ao escopo de manutenção
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Serviços Relacionados */}
        <section className="relative py-16 overflow-hidden border-t border-gp-graphite-lighter bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-lg font-bold text-white uppercase tracking-wider">Serviços Relacionados</h2>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/mecanica-industrial"
                  className="inline-flex items-center gap-2 bg-gp-graphite border border-gp-graphite-lighter hover:border-gp-yellow/30 px-5 py-3 rounded-lg text-gray-300 hover:text-gp-yellow text-sm font-medium transition-all duration-300"
                >
                  <i className="ri-arrow-right-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                  Mecânica Industrial
                </Link>
                <Link
                  to="/servicos-de-campo"
                  className="inline-flex items-center gap-2 bg-gp-graphite border border-gp-graphite-lighter hover:border-gp-yellow/30 px-5 py-3 rounded-lg text-gray-300 hover:text-gp-yellow text-sm font-medium transition-all duration-300"
                >
                  <i className="ri-arrow-right-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                  Serviços de Campo
                </Link>
                <Link
                  to="/caldeiraria-solda"
                  className="inline-flex items-center gap-2 bg-gp-graphite border border-gp-graphite-lighter hover:border-gp-yellow/30 px-5 py-3 rounded-lg text-gray-300 hover:text-gp-yellow text-sm font-medium transition-all duration-300"
                >
                  <i className="ri-arrow-right-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                  Caldeiraria e Solda
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="relative py-20 bg-gp-graphite">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="relative rounded-xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-gp-yellow/20 to-gp-graphite"></div>
              <div className="absolute inset-0 bg-gp-graphite/60"></div>
              <div className="relative z-10 p-8 md:p-12 text-center">
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase mb-4">
                  Solicite uma Avaliação Técnica
                </h2>
                <p className="text-gray-300 max-w-2xl mx-auto mb-8 leading-relaxed">
                  Entre em contato com a Pilar e receba um diagnóstico especializado para sua próxima Parada Geral. Especialistas em substituição de correntes industriais, manutenção de transportadores e serviços integrados de caldeiraria e soldagem em plantas de papel e celulose.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a
                    href="https://wa.me/5527993092922"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 bg-gp-yellow hover:bg-gp-yellow-dark text-gp-graphite px-8 py-4 rounded-full font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap"
                  >
                    <div className="w-10 h-10 bg-gp-graphite rounded-full flex items-center justify-center">
                      <i className="ri-whatsapp-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <span>Solicitar Orçamento</span>
                    <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
                  </a>
                  <Link
                    to="/contato"
                    className="inline-flex items-center justify-center gap-2 border border-gray-500 hover:border-gp-yellow text-gray-300 hover:text-gp-yellow px-8 py-4 rounded-full font-semibold text-sm transition-all duration-300 whitespace-nowrap"
                  >
                    <i className="ri-mail-line text-lg w-6 h-6 flex items-center justify-center"></i>
                    Falar com Especialista
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Back to Cases */}
        <section className="py-10 border-t border-gp-graphite-lighter">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <Link
                to="/cases"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider"
              >
                <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center"></i>
                Voltar para Cases
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider"
              >
                <i className="ri-home-line w-5 h-5 flex items-center justify-center"></i>
                Voltar para Home
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}